import type { AuthResponse, Paginated } from '@/types/api'
import { tokenStore } from './tokens'

const BASE_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:3000/api/v1'

export class ApiError extends Error {
  readonly statusCode: number
  readonly details?: string[]

  constructor(message: string, statusCode: number, details?: string[]) {
    super(message)
    this.name = 'ApiError'
    this.statusCode = statusCode
    this.details = details
  }
}

interface ErrorEnvelope {
  error: { statusCode: number; message: string | string[]; error: string }
}

interface SuccessEnvelope<T> {
  data: T
  meta?: { total: number; page: number; limit: number }
}

type RequestOptions = Omit<RequestInit, 'body'> & {
  body?: unknown
  /** Skip attaching the access token / refresh-on-401 (used by auth endpoints themselves). */
  skipAuth?: boolean
}

let refreshPromise: Promise<string | null> | null = null

async function refreshAccessToken(): Promise<string | null> {
  const refreshToken = tokenStore.getRefreshToken()
  if (!refreshToken) return null

  if (!refreshPromise) {
    refreshPromise = fetch(`${BASE_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    })
      .then(async (response) => {
        if (!response.ok) {
          tokenStore.clear()
          return null
        }
        const envelope = (await response.json()) as SuccessEnvelope<
          Pick<AuthResponse, 'accessToken' | 'refreshToken'>
        >
        tokenStore.set(envelope.data)
        return envelope.data.accessToken
      })
      .catch(() => {
        tokenStore.clear()
        return null
      })
      .finally(() => {
        refreshPromise = null
      })
  }

  return refreshPromise
}

async function parseEnvelope<T>(
  response: Response,
): Promise<SuccessEnvelope<T>> {
  if (response.status === 204) {
    return { data: undefined as T }
  }

  const json = (await response.json()) as SuccessEnvelope<T> | ErrorEnvelope

  if (!response.ok || 'error' in json) {
    const errorBody = 'error' in json ? json.error : null
    const message = Array.isArray(errorBody?.message)
      ? (errorBody?.message[0] ?? 'Something went wrong')
      : (errorBody?.message ?? 'Something went wrong')
    const details = Array.isArray(errorBody?.message)
      ? errorBody.message
      : undefined
    throw new ApiError(
      message,
      errorBody?.statusCode ?? response.status,
      details,
    )
  }

  return json
}

async function performRequest(
  path: string,
  { body, skipAuth, headers, ...options }: RequestOptions,
): Promise<Response> {
  const doFetch = async (): Promise<Response> => {
    const finalHeaders = new Headers(headers)
    if (body !== undefined) finalHeaders.set('Content-Type', 'application/json')
    if (!skipAuth) {
      const accessToken = tokenStore.getAccessToken()
      if (accessToken)
        finalHeaders.set('Authorization', `Bearer ${accessToken}`)
    }

    return fetch(`${BASE_URL}${path}`, {
      ...options,
      headers: finalHeaders,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    })
  }

  let response = await doFetch()

  if (response.status === 401 && !skipAuth && tokenStore.getRefreshToken()) {
    const refreshed = await refreshAccessToken()
    if (refreshed) {
      response = await doFetch()
    }
  }

  return response
}

// XMLHttpRequest, not fetch, because fetch exposes no upload-progress event —
// the only way to report "X% uploaded" for a request body is xhr.upload.onprogress.
function xhrUpload(
  path: string,
  formData: FormData,
  accessToken: string | null,
  onProgress?: (percent: number) => void,
): Promise<Response> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('POST', `${BASE_URL}${path}`)
    if (accessToken) xhr.setRequestHeader('Authorization', `Bearer ${accessToken}`)
    if (onProgress) {
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          onProgress(Math.round((event.loaded / event.total) * 100))
        }
      }
    }
    // Wrap the XHR result as a Response so callers (parseEnvelope) don't
    // need a separate code path for the xhr-vs-fetch cases.
    xhr.onload = () => resolve(new Response(xhr.responseText, { status: xhr.status }))
    xhr.onerror = () => reject(new ApiError('Network error during upload', 0))
    xhr.send(formData)
  })
}

async function performUpload(
  path: string,
  formData: FormData,
  onProgress?: (percent: number) => void,
): Promise<Response> {
  const doUpload = async (): Promise<Response> => {
    const accessToken = tokenStore.getAccessToken()
    if (onProgress) return xhrUpload(path, formData, accessToken, onProgress)
    const headers = new Headers()
    if (accessToken) headers.set('Authorization', `Bearer ${accessToken}`)
    // No Content-Type here: the browser sets multipart/form-data with the
    // right boundary itself when the body is a FormData instance.
    return fetch(`${BASE_URL}${path}`, { method: 'POST', headers, body: formData })
  }

  let response = await doUpload()

  if (response.status === 401 && tokenStore.getRefreshToken()) {
    const refreshed = await refreshAccessToken()
    if (refreshed) {
      response = await doUpload()
    }
  }

  return response
}

/** For multipart/form-data uploads — apiFetch always JSON-encodes its body. */
export async function apiUpload<T>(
  path: string,
  formData: FormData,
  onProgress?: (percent: number) => void,
): Promise<T> {
  const envelope = await parseEnvelope<T>(
    await performUpload(path, formData, onProgress),
  )
  return envelope.data
}

export async function apiFetch<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const envelope = await parseEnvelope<T>(await performRequest(path, options))
  return envelope.data
}

export async function apiFetchPaginated<T>(
  path: string,
  options: RequestOptions = {},
): Promise<Paginated<T>> {
  const envelope = await parseEnvelope<T[]>(await performRequest(path, options))
  return {
    items: envelope.data,
    total: envelope.meta?.total ?? envelope.data.length,
    page: envelope.meta?.page ?? 1,
    limit: envelope.meta?.limit ?? envelope.data.length,
  }
}

export const api = {
  get: <T>(path: string, options?: RequestOptions) =>
    apiFetch<T>(path, { ...options, method: 'GET' }),
  getPaginated: <T>(path: string, options?: RequestOptions) =>
    apiFetchPaginated<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    apiFetch<T>(path, { ...options, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    apiFetch<T>(path, { ...options, method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    apiFetch<T>(path, { ...options, method: 'PUT', body }),
  delete: <T>(path: string, options?: RequestOptions) =>
    apiFetch<T>(path, { ...options, method: 'DELETE' }),
}
